import React, { useState, useEffect, useRef } from 'react'
import Tree from 'react-d3-tree'

interface SecurityTreeProps {
  data: any
  isPrintMode?: boolean
}

const SecurityTreeComponent: React.FC<SecurityTreeProps> = ({
  data,
  isPrintMode = false,
}) => {
  const treeContainerRef = useRef<HTMLDivElement>(null)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)

  const formatData = (data: any) => {
    return {
      name: data?.Label,
      children:
        data?.SecurityControls?.map((control: any) => ({
          name: control?.Label,
          children:
            control.ExistingControls?.map((existingControl: any) => ({
              name: `${existingControl?.Label} (${existingControl.Description})`,
            })) || [],
        })) || [],
    }
  }

  const formattedData = formatData(data)

  const renderCustomNode = ({ nodeDatum }: any) => (
    <g>
      <circle r="15" fill="lightsteelblue" stroke="steelblue" strokeWidth="2" />
      <foreignObject x="20" y="-20" width="200" height="60">
        <div
          style={{
            fontSize: '11px',
            fontWeight: 'normal',
            color: '#333',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '180px',
            maxHeight: '55px',
          }}
        >
          {nodeDatum.name}
        </div>
      </foreignObject>
    </g>
  )

  useEffect(() => {
    if (treeContainerRef.current) {
      const { offsetWidth, offsetHeight } = treeContainerRef.current
      // Position the root node at the top center with some padding
      setTranslate({
        x: offsetWidth / 2,
        y: 100,
      })
      // Set initial zoom based on number of children
      const childCount = formattedData?.children?.length || 0
      if (childCount > 15) {
        setZoom(0.5)
      } else if (childCount > 10) {
        setZoom(0.6)
      } else if (childCount > 5) {
        setZoom(0.7)
      } else {
        setZoom(0.8)
      }
    }
  }, [data])

  const handleZoomIn = () => {
    setZoom((prevZoom) => Math.min(prevZoom + 0.1, 2))
  }

  const handleZoomOut = () => {
    setZoom((prevZoom) => Math.max(prevZoom - 0.1, 0.1))
  }

  const handleResetView = () => {
    if (treeContainerRef.current) {
      const { offsetWidth } = treeContainerRef.current
      setTranslate({
        x: offsetWidth / 2,
        y: 100,
      })
      const childCount = formattedData?.children?.length || 0
      if (childCount > 15) {
        setZoom(0.5)
      } else if (childCount > 10) {
        setZoom(0.6)
      } else if (childCount > 5) {
        setZoom(0.7)
      } else {
        setZoom(0.8)
      }
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Zoom Controls - Only show in interactive mode */}
      {!isPrintMode && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            background: 'white',
            padding: '8px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <button
            onClick={handleZoomIn}
            style={{
              padding: '6px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
            }}
            title="Zoom In"
          >
            +
          </button>
          <button
            onClick={handleZoomOut}
            style={{
              padding: '6px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
            }}
            title="Zoom Out"
          >
            −
          </button>
          <button
            onClick={handleResetView}
            style={{
              padding: '6px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '12px',
            }}
            title="Reset View"
          >
            ↻
          </button>
        </div>
      )}

      {/* Tree Container */}
      <div
        ref={treeContainerRef}
        style={{
          width: '100%',
          height: isPrintMode ? '600px' : '700px',
          border: isPrintMode ? 'none' : '1px solid #e8e8e8',
          borderRadius: isPrintMode ? '0' : '8px',
          position: 'relative',
          overflow: 'hidden',
          background: isPrintMode ? 'white' : '#fafafa',
        }}
      >
        <Tree
          data={formattedData}
          renderCustomNodeElement={renderCustomNode}
          orientation="vertical"
          pathFunc="step"
          nodeSize={{ x: 250, y: 180 }}
          separation={{ siblings: 1.2, nonSiblings: 1.5 }}
          zoomable={!isPrintMode}
          draggable={!isPrintMode}
          translate={translate}
          zoom={zoom}
          onUpdate={
            !isPrintMode
              ? ({ translate, zoom }) => {
                  setTranslate(translate)
                  setZoom(zoom)
                }
              : undefined
          }
          scaleExtent={{ min: 0.1, max: 2 }}
          enableLegacyTransitions={true}
        />
      </div>
    </div>
  )
}

export default SecurityTreeComponent
